import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Button, Row, Col, Divider } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import SprayJobCreateWrapper from './SprayJobCreate.styles';

const { Option } = Select;

/**
 * Task-stage options shown in the per-task "stage" dropdown. The values are
 * the underlying job-status strings (kept stable for backward compatibility
 * with already-saved tasks), but the labels are the operator-facing phase
 * names — "Prep", "Spray", "Cleanup" — instead of the verb-shaped action
 * labels reused from sprayJobStates.action (which read as the button that
 * triggers the next transition, not the page the task appears on).
 *
 * The Cleanup page already collapses spray_cleanup + waiting_for_completion
 * into one screen, so we deliberately do NOT offer a separate "Finish"
 * option here — Damien's PR #417 follow-up: "Cleanup/final are the same
 * page so it's pointless having them assigned separately."
 */
// Round-6 (Damien): the "Spray" stage is gone — operators don't need
// extra tasks during a spray. Just Prep + Cleanup remain. Legacy
// 'prepared' / 'spraying' values still readable elsewhere for back-
// compat with already-saved jobs, but the dropdown no longer offers
// them.
const TASK_STAGE_OPTIONS = [
  { value: 'to_be_started', label: 'Prep' },
  { value: 'spray_cleanup', label: 'Cleanup' },
];

/**
 * Normalize PPE data from old (flat array) or new (object with preparation/spraying) format.
 * @param {Array|Object} ppeData
 * @returns {{ preparation: string[], spraying: string[] }}
 */
const normalizePPE = (ppeData) => {
  if (!ppeData) return { preparation: [], spraying: [] };

  // New format: { preparation: [...], spraying: [...] }
  if (ppeData.preparation || ppeData.spraying) {
    return {
      preparation: ppeData.preparation || [],
      spraying: ppeData.spraying || [],
    };
  }

  // Old format: flat array — treat as preparation PPE for backward compat
  if (Array.isArray(ppeData)) {
    return { preparation: ppeData, spraying: [] };
  }

  return { preparation: [], spraying: [] };
};

const PermissionTaskModal = ({ visible, onCancel, onSave, initialData }) => {
  const DEFAULT_PPE_OPTIONS = [
    "A/C Cab",
    "Overalls",
    "Face Shield",
    "PVC Apron",
    "Respirator",
    "Goggles",
    "PVC Boots & Gloves"
  ];

  const [form] = Form.useForm();
  const [preparationPPE, setPreparationPPE] = useState([]);
  const [sprayingPPE, setSprayingPPE] = useState([]);
  const [tasks, setTasks] = useState([{ task_name: '', mapping_status: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Populate modal with initial data when visible or initialData changes
  useEffect(() => {
    if (visible && initialData) {
      // Reset first to ensure clean state
      setPreparationPPE([]);
      setSprayingPPE([]);
      setTasks([{ task_name: null, mapping_status: null }]);

      // Then set the data in the next tick to ensure state update
      setTimeout(() => {
        // Load saved PPE requirements from template (handle old and new format)
        const normalized = normalizePPE(initialData.ppeRequirements);
        setPreparationPPE([...normalized.preparation]);
        setSprayingPPE([...normalized.spraying]);

        if (initialData.tasks && initialData.tasks.length > 0) {
          setTasks([...initialData.tasks]);
        } else {
          setTasks([{ task_name: null, mapping_status: null }]);
        }
      }, 0);
    } else if (visible && !initialData) {
      // Reset to default when opening without initial data
      setPreparationPPE([]);
      setSprayingPPE([]);
      setTasks([{ task_name: null, mapping_status: null }]);
    }
  }, [visible, initialData]);

  const addTask = () => {
    setTasks([...tasks, { task_name: null, mapping_status: null }]);
  };

  const removeTask = (index) => {
    if (tasks.length > 1) {
      const updatedTasks = tasks.filter((_, i) => i !== index);
      setTasks(updatedTasks);
    }
  };

  const handleTaskChange = (index, field, value) => {
    const updatedTasks = [...tasks];
    updatedTasks[index] = { ...updatedTasks[index], [field]: value };
    setTasks(updatedTasks);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setShowValidation(true);

      // Validate at least one PPE in either category
      if (preparationPPE.length === 0 && sprayingPPE.length === 0) {
        throw new Error('Please enter at least one PPE requirement');
      }

      // Tasks are optional, but any task row that has been started must be
      // complete (both name and mapping_status filled).
      const startedButIncomplete = tasks.some(task => {
        const hasName = task.task_name && task.task_name.trim();
        const hasStatus = !!task.mapping_status;
        return (hasName && !hasStatus) || (!hasName && hasStatus);
      });
      if (startedButIncomplete) {
        throw new Error('Please complete or remove partially filled task rows');
      }

      // Filter out completely empty task rows before saving
      const cleanedTasks = tasks.filter(task =>
        (task.task_name && task.task_name.trim()) || task.mapping_status
      );

      // Save PPE as structured object with both phases
      const ppeRequirements = {
        preparation: preparationPPE,
        spraying: sprayingPPE,
      };

      await onSave({ ppeRequirements, tasks: cleanedTasks });
      setShowValidation(false);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowValidation(false);
    onCancel();
  };

  return (
    <SprayJobCreateWrapper>
      <Modal
        title="Permission & Task Details"
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-button">
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            className="btn-success"
            loading={isSubmitting}
          >
            Save
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {/* Preparation PPE */}
          <Form.Item
            label="Preparation PPE"
            required
            validateStatus={showValidation && preparationPPE.length === 0 && sprayingPPE.length === 0 ? 'error' : ''}
            help={showValidation && preparationPPE.length === 0 && sprayingPPE.length === 0 ? 'Please enter at least one PPE requirement' : ''}
          >
            <Select
              key={`prep-ppe-${visible}-${JSON.stringify(preparationPPE)}`}
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Select or enter preparation PPE"
              value={preparationPPE}
              onChange={(value) => {
                const filteredValues = value.filter(item => item && item.trim().length > 0);
                setPreparationPPE(filteredValues);
              }}
              size="large"
              notFoundContent={null}
              showArrow={false}
            >
              {DEFAULT_PPE_OPTIONS.map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Spraying PPE */}
          <Form.Item
            label="Spraying PPE"
          >
            <Select
              key={`spray-ppe-${visible}-${JSON.stringify(sprayingPPE)}`}
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Select or enter spraying PPE"
              value={sprayingPPE}
              onChange={(value) => {
                const filteredValues = value.filter(item => item && item.trim().length > 0);
                setSprayingPPE(filteredValues);
              }}
              size="large"
              notFoundContent={null}
              showArrow={false}
            >
              {DEFAULT_PPE_OPTIONS.map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider />

          {/* Tasks */}
          <Form.Item
            label="Tasks"
            required
            validateStatus={showValidation && tasks.some(t => !t.task_name || !t.task_name.trim() || !t.mapping_status) ? 'error' : ''}
            help={showValidation && tasks.some(t => !t.task_name || !t.task_name.trim() || !t.mapping_status) ? 'Please fill in all task fields' : ''}
          >
            {tasks.map((task, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "16px",
                  padding: "16px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  backgroundColor: '#fafafa'
                }}
              >
                <Row gutter={[16, 16]} style={{ display: "flex", alignItems: "flex-start" }}>
                  <Col span={10}>
                    <Form.Item
                      required
                      validateStatus={showValidation && (!task.task_name || !task.task_name.trim()) ? 'error' : ''}
                      help={showValidation && (!task.task_name || !task.task_name.trim()) ? 'Task name is required' : ''}
                    >
                      <Input
                        placeholder="Task name"
                        value={task.task_name}
                        onChange={(e) =>
                          handleTaskChange(index, "task_name", e.target.value)
                        }
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      required
                      validateStatus={showValidation && !task.mapping_status ? 'error' : ''}
                      help={showValidation && !task.mapping_status ? 'Mapping status is required' : ''}
                    >
                      <Select
                        placeholder="Stage"
                        value={task.mapping_status}
                        onChange={(value) =>
                            handleTaskChange(index, "mapping_status", value)
                        }
                        style={{ width: "100%" }}
                        size="large"
                      >
                        {TASK_STAGE_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeTask(index)}
                      disabled={tasks.length <= 1}
                      style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: '4px'
                      }}
                    />
                  </Col>
                </Row>
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addTask}
              style={{ width: "100%" }}
              size="large"
            >
              Add Task
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </SprayJobCreateWrapper>
  );
};

export default PermissionTaskModal;
